import React, { useEffect, useMemo, useState } from "react";
import { Cpu, Save, ToggleLeft, ToggleRight, Wand2 } from "lucide-react";
import {
  agentApiService,
  AiAgentSettings,
  ChannelInstructions,
  ChannelType,
} from "../../services/api";

const OPENAI_MODEL_OPTIONS = [
  "gpt-5.1",
  "gpt-5",
  "gpt-5-mini",
  "gpt-5-nano",
  "gpt-4.1",
  "gpt-4.1-mini",
  "gpt-4o",
  "gpt-4o-mini",
];

const GEMINI_MODEL_OPTIONS = [
  "gemini-2.5-pro",
  "gemini-2.5-flash",
  "gemini-2.5-flash-lite",
  "gemini-2.0-flash",
  "gemini-2.0-flash-lite",
];

const CHANNEL_META: Array<{ key: ChannelType; title: string }> = [
  { key: "whatsapp", title: "WhatsApp" },
  { key: "facebook_messenger", title: "Facebook Messenger" },
  { key: "instagram", title: "Instagram" },
];

const DEFAULT_CHANNEL_INSTRUCTIONS: ChannelInstructions = {
  whatsapp: {
    systemInstruction:
      "You are a sales agent for Tamiyouz. Be helpful, concise, and focus on qualifying leads and booking meetings.",
    followUpInstruction:
      "If the lead goes silent, follow up politely within the conversation context.",
    nonReplyLevel: 0,
  },
  facebook_messenger: {
    systemInstruction:
      "You are a sales agent for Tamiyouz. Be helpful, concise, and focus on qualifying leads and booking meetings.",
    followUpInstruction:
      "If the lead goes silent, follow up politely within the conversation context.",
    nonReplyLevel: 0,
  },
  instagram: {
    systemInstruction:
      "You are a sales agent for Tamiyouz. Be helpful, concise, and focus on qualifying leads and booking meetings.",
    followUpInstruction:
      "If the lead goes silent, follow up politely within the conversation context.",
    nonReplyLevel: 0,
  },
};

const DEFAULT_SETTINGS: AiAgentSettings = {
  key: "default",
  aiEnabled: true,
  channelInstructions: DEFAULT_CHANNEL_INSTRUCTIONS,
  modelProvider: "gemini",
  modelTier: "cheap",
  openaiModel: "",
  geminiModel: "gemini-2.5-flash",
  kbEnabled: false,
  kbTopK: 4,
};

const DEFAULT_TEST_MESSAGE_BY_CHANNEL: Record<ChannelType, string> = {
  whatsapp: "",
  facebook_messenger: "",
  instagram: "",
};

const DEFAULT_TEST_REPLY_STATE_BY_CHANNEL: Record<
  ChannelType,
  { loading: boolean; reply: string; error: string | null }
> = {
  whatsapp: { loading: false, reply: "", error: null },
  facebook_messenger: { loading: false, reply: "", error: null },
  instagram: { loading: false, reply: "", error: null },
};

function mergeSettings(data?: Partial<AiAgentSettings>): AiAgentSettings {
  const source = data || {};
  const incoming = source.channelInstructions || DEFAULT_CHANNEL_INSTRUCTIONS;

  const channelInstructions: ChannelInstructions = {
    whatsapp: {
      ...DEFAULT_CHANNEL_INSTRUCTIONS.whatsapp,
      ...(incoming.whatsapp || {}),
    },
    facebook_messenger: {
      ...DEFAULT_CHANNEL_INSTRUCTIONS.facebook_messenger,
      ...(incoming.facebook_messenger || {}),
    },
    instagram: {
      ...DEFAULT_CHANNEL_INSTRUCTIONS.instagram,
      ...(incoming.instagram || {}),
    },
  };

  return {
    ...DEFAULT_SETTINGS,
    ...source,
    channelInstructions,
  };
}

export const AgentSettingsPage: React.FC = () => {
  const [settings, setSettings] = useState<AiAgentSettings>(DEFAULT_SETTINGS);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saveMessage, setSaveMessage] = useState<{
    type: "success" | "error";
    text: string;
  } | null>(null);
  const [testMessages, setTestMessages] = useState<Record<ChannelType, string>>(
    DEFAULT_TEST_MESSAGE_BY_CHANNEL,
  );
  const [testReplies, setTestReplies] = useState<
    Record<ChannelType, { loading: boolean; reply: string; error: string | null }>
  >(DEFAULT_TEST_REPLY_STATE_BY_CHANNEL);

  const loadSettings = async () => {
    setLoading(true);
    try {
      const data = await agentApiService.getSettings();
      setSettings(mergeSettings(data));
    } catch (error) {
      console.error("Failed to load AI settings:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSettings();
  }, []);

  const modelOptions = useMemo(
    () =>
      settings.modelProvider === "openai"
        ? OPENAI_MODEL_OPTIONS
        : GEMINI_MODEL_OPTIONS,
    [settings.modelProvider],
  );

  const handleSettingsChange = (key: keyof AiAgentSettings, value: any) => {
    setSettings((prev) => ({ ...prev, [key]: value }));
    setSaveMessage(null);
  };

  const normalizeNonReplyLevel = (value: unknown): number => {
    const parsed = Number(value);
    if (!Number.isFinite(parsed)) {
      return 0;
    }
    return Math.min(3, Math.max(0, Math.trunc(parsed)));
  };

  const handleChannelInstructionTextChange = (
    channel: ChannelType,
    field: "systemInstruction" | "followUpInstruction",
    value: string,
  ) => {
    setSettings((prev) => ({
      ...prev,
      channelInstructions: {
        ...prev.channelInstructions,
        [channel]: {
          ...prev.channelInstructions[channel],
          [field]: value,
        },
      },
    }));
    setSaveMessage(null);
  };

  const handleChannelNonReplyLevelChange = (
    channel: ChannelType,
    value: string,
  ) => {
    const nonReplyLevel = normalizeNonReplyLevel(value);
    setSettings((prev) => ({
      ...prev,
      channelInstructions: {
        ...prev.channelInstructions,
        [channel]: {
          ...prev.channelInstructions[channel],
          nonReplyLevel,
        },
      },
    }));
    setSaveMessage(null);
  };

  const buildSettingsPayload = (
    current: AiAgentSettings,
  ): Partial<AiAgentSettings> => {
    const channelInstructions: ChannelInstructions = {
      whatsapp: {
        systemInstruction:
          current.channelInstructions.whatsapp.systemInstruction.trim(),
        followUpInstruction:
          current.channelInstructions.whatsapp.followUpInstruction.trim(),
        nonReplyLevel: normalizeNonReplyLevel(
          current.channelInstructions.whatsapp.nonReplyLevel,
        ),
      },
      facebook_messenger: {
        systemInstruction:
          current.channelInstructions.facebook_messenger.systemInstruction.trim(),
        followUpInstruction:
          current.channelInstructions.facebook_messenger.followUpInstruction.trim(),
        nonReplyLevel: normalizeNonReplyLevel(
          current.channelInstructions.facebook_messenger.nonReplyLevel,
        ),
      },
      instagram: {
        systemInstruction:
          current.channelInstructions.instagram.systemInstruction.trim(),
        followUpInstruction:
          current.channelInstructions.instagram.followUpInstruction.trim(),
        nonReplyLevel: normalizeNonReplyLevel(
          current.channelInstructions.instagram.nonReplyLevel,
        ),
      },
    };

    return {
      aiEnabled: current.aiEnabled,
      channelInstructions,
      modelProvider: current.modelProvider,
      modelTier: current.modelTier,
      openaiModel: current.openaiModel?.trim() || undefined,
      geminiModel: current.geminiModel?.trim() || undefined,
      openaiApiKey: current.openaiApiKey?.trim() || undefined,
      geminiApiKey: current.geminiApiKey?.trim() || undefined,
      kbEnabled: current.kbEnabled,
      kbTopK: current.kbTopK,
    };
  };

  const saveSettings = async () => {
    setSaving(true);
    setSaveMessage(null);
    try {
      const payload = buildSettingsPayload(settings);
      const updated = await agentApiService.updateSettings(payload);
      setSettings(mergeSettings(updated));
      setSaveMessage({ type: "success", text: "Settings saved successfully." });
    } catch (error: any) {
      const backendMessage =
        error?.response?.data?.message || error?.message || "Save failed.";
      console.error("Failed to save AI settings:", error);
      setSaveMessage({
        type: "error",
        text: String(backendMessage),
      });
    } finally {
      setSaving(false);
    }
  };

  const runChannelTest = async (channel: ChannelType) => {
    const entry = settings.channelInstructions[channel];
    const systemInstruction = entry.systemInstruction.trim();
    const followUpInstruction = entry.followUpInstruction.trim();
    const nonReplyLevel = normalizeNonReplyLevel(entry.nonReplyLevel);
    const testMessage = (testMessages[channel] || "").trim();

    if (!systemInstruction || !followUpInstruction) {
      setTestReplies((prev) => ({
        ...prev,
        [channel]: {
          loading: false,
          reply: "",
          error: "System and follow-up instructions are required.",
        },
      }));
      return;
    }

    if (!testMessage) {
      setTestReplies((prev) => ({
        ...prev,
        [channel]: {
          loading: false,
          reply: "",
          error: "Please write a test message first.",
        },
      }));
      return;
    }

    setTestReplies((prev) => ({
      ...prev,
      [channel]: { loading: true, reply: "", error: null },
    }));

    try {
      const result = await agentApiService.testSettingsReply({
        channel,
        systemInstruction,
        followUpInstruction,
        nonReplyLevel,
        testMessage,
      });

      setTestReplies((prev) => ({
        ...prev,
        [channel]: {
          loading: false,
          reply: result.reply || "",
          error: null,
        },
      }));
    } catch (error: any) {
      const backendMessage =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        "Failed to run AI test.";
      setTestReplies((prev) => ({
        ...prev,
        [channel]: {
          loading: false,
          reply: "",
          error: String(backendMessage),
        },
      }));
    }
  };

  return (
    <div className="page-section">
      <header className="page-heading">
        <div>
          <p className="eyebrow">AI Sales Agent</p>
          <h2>Settings & playbook</h2>
        </div>
        <div className="heading-actions">
          <button
            className="primary-button"
            onClick={saveSettings}
            disabled={saving || loading}
          >
            <Save size={16} />
            {saving ? "Saving..." : "Save Settings"}
          </button>
          {saveMessage && (
            <span
              style={{
                fontSize: "0.85rem",
                marginLeft: "0.75rem",
                color:
                  saveMessage.type === "success"
                    ? "var(--success, #15803d)"
                    : "var(--danger, #dc2626)",
              }}
            >
              {saveMessage.text}
            </span>
          )}
        </div>
      </header>

      <div className="settings-grid">
        <section className="panel">
          <header className="panel-header">
            <div>
              <p className="eyebrow">
                <Cpu size={14} /> Model & Channel Playbooks
              </p>
              <h3>AI behavior</h3>
            </div>
          </header>

          <div className="form-group">
            <label>AI Enabled</label>
            <button
              className="ghost-button"
              onClick={() =>
                handleSettingsChange("aiEnabled", !settings.aiEnabled)
              }
            >
              {settings.aiEnabled ? (
                <ToggleRight size={18} />
              ) : (
                <ToggleLeft size={18} />
              )}
              {settings.aiEnabled ? "Enabled" : "Disabled"}
            </button>
          </div>

          <div className="form-group">
            <label>Model Provider</label>
            <select
              className="input-field"
              value={settings.modelProvider}
              onChange={(e) =>
                handleSettingsChange("modelProvider", e.target.value)
              }
            >
              <option value="gemini">Gemini</option>
              <option value="openai">OpenAI</option>
            </select>
          </div>

          <div className="form-group">
            <label>Model</label>
            <select
              className="input-field"
              value={
                settings.modelProvider === "openai"
                  ? settings.openaiModel || ""
                  : settings.geminiModel || ""
              }
              onChange={(e) =>
                handleSettingsChange(
                  settings.modelProvider === "openai"
                    ? "openaiModel"
                    : "geminiModel",
                  e.target.value,
                )
              }
            >
              {modelOptions.map((model) => (
                <option key={model} value={model}>
                  {model}
                </option>
              ))}
            </select>
          </div>

          <div style={{ display: "grid", gap: "1rem", marginTop: "1rem" }}>
            {CHANNEL_META.map((channel) => {
              const entry = settings.channelInstructions[channel.key];
              const testState = testReplies[channel.key];
              return (
                <div
                  key={channel.key}
                  style={{
                    border: "1px solid var(--border)",
                    borderRadius: "var(--radius-md)",
                    padding: "1rem",
                    display: "grid",
                    gap: "0.75rem",
                    background: "var(--surface-muted)",
                  }}
                >
                  <h4 style={{ margin: 0 }}>{channel.title} Instructions</h4>

                  <div className="form-group">
                    <label>System Instruction</label>
                    <textarea
                      className="input-field"
                      rows={4}
                      value={entry.systemInstruction || ""}
                      onChange={(e) =>
                        handleChannelInstructionTextChange(
                          channel.key,
                          "systemInstruction",
                          e.target.value,
                        )
                      }
                    />
                  </div>

                  <div className="form-group">
                    <label>Follow-up Instruction</label>
                    <textarea
                      className="input-field"
                      rows={3}
                      value={entry.followUpInstruction || ""}
                      onChange={(e) =>
                        handleChannelInstructionTextChange(
                          channel.key,
                          "followUpInstruction",
                          e.target.value,
                        )
                      }
                    />
                  </div>

                  <div className="form-group">
                    <label>Non-reply Level (Saved)</label>
                    <select
                      className="input-field"
                      value={String(normalizeNonReplyLevel(entry.nonReplyLevel))}
                      onChange={(e) =>
                        handleChannelNonReplyLevelChange(channel.key, e.target.value)
                      }
                    >
                      <option value="0">0 - Default filtering only</option>
                      <option value="1">1 - Skip acknowledgements only</option>
                      <option value="2">2 - Skip low-intent social messages</option>
                      <option value="3">3 - Aggressive no-reply filtering</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Test Message</label>
                    <textarea
                      className="input-field"
                      rows={2}
                      value={testMessages[channel.key]}
                      onChange={(e) =>
                        setTestMessages((prev) => ({
                          ...prev,
                          [channel.key]: e.target.value,
                        }))
                      }
                      placeholder={`Write a sample ${channel.title} customer message`}
                    />
                  </div>

                  <div style={{ display: "flex", justifyContent: "flex-end" }}>
                    <button
                      type="button"
                      className="ghost-button"
                      onClick={() => runChannelTest(channel.key)}
                      disabled={testState.loading}
                    >
                      <Wand2 size={16} />
                      {testState.loading ? "Testing..." : "Test AI"}
                    </button>
                  </div>

                  {testState.error && (
                    <small style={{ color: "var(--danger, #dc2626)" }}>
                      {testState.error}
                    </small>
                  )}
                  {testState.reply && (
                    <div
                      style={{
                        border: "1px solid var(--border)",
                        borderRadius: "var(--radius-md)",
                        padding: "0.75rem",
                        background: "var(--surface)",
                        whiteSpace: "pre-wrap",
                        color: "var(--text-primary)",
                      }}
                    >
                      <strong style={{ display: "block", marginBottom: "0.4rem" }}>
                        AI Reply
                      </strong>
                      {testState.reply}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </section>

        <section className="panel">
          <header className="panel-header">
            <div>
              <p className="eyebrow">
                <Cpu size={14} /> API Keys
              </p>
              <h3>Model credentials</h3>
            </div>
          </header>

          <div className="form-group">
            <label>OpenAI API Key</label>
            <input
              className="input-field"
              type="password"
              value={settings.openaiApiKey || ""}
              onChange={(e) =>
                handleSettingsChange("openaiApiKey", e.target.value)
              }
              placeholder="sk-..."
            />
          </div>

          <div className="form-group">
            <label>Gemini API Key</label>
            <input
              className="input-field"
              type="password"
              value={settings.geminiApiKey || ""}
              onChange={(e) =>
                handleSettingsChange("geminiApiKey", e.target.value)
              }
              placeholder="AIza..."
            />
          </div>
        </section>
      </div>
    </div>
  );
};
